import { goApi } from './apiService';
import { API_BASE_URL } from '../common/constants';

/**
 * Authentication API Types
 */
export interface LoginCredentials {
    phone: string;
    password: string;
    session_id?: string;
}

export interface LoginResponse {
    data: UserData;
    token?: string;
}

export interface UserData {
    id: string;
    phone: string;
    name?: string;
    email?: string;
    role?: string;
    [key: string]: any;
}

export interface PermissionsAccessResponse {
    data: any;
}

/**
 * Authentication API Service
 */
export const authApi = {
    /**
     * Login user
     * Note: This uses direct fetch because we need to extract the token from response headers
     */
    login: async (
        credentials: LoginCredentials,
    ): Promise<{ token: string; userData: UserData }> => {
        const response = await fetch(`${API_BASE_URL}/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                phone: credentials.phone,
                password: credentials.password,
                spirit_code: 'asdiajsdojasodij',
                platform: 'WEB',
            }),
        });

        const token = response.headers.get('authorization')?.split(' ')[1];

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Login failed');
        }

        const result = await response.json();
        const userData = result.data || result;

        if (!token) {
            throw new Error('No token received from server');
        }

        return { token, userData };
    },

    /**
     * Get current user access permissions
     */
    getUserPermissions: async (token: string): Promise<any> => {
        const response = await goApi.get<PermissionsAccessResponse>('/permissions/my-permissions', {
            requireAuth: true,
        });

        return response.data;
    },

    /**
     * Logout user (client-side cleanup)
     */
    logout: (): void => {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('permissions');
    },

    /**
     * Check if user is authenticated
     */
    isAuthenticated: (): boolean => {
        return !!localStorage.getItem('token') && !!localStorage.getItem('user');
    },

    /**
     * Get stored user data
     */
    getUser: (): UserData | null => {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    },

    /**
     * Get stored token
     */
    getToken: (): string | null => {
        return localStorage.getItem('token');
    },

    /**
     * Store user data
     */
    setUser: (userData: UserData): void => {
        localStorage.setItem('user', JSON.stringify(userData));
    },

    /**
     * Store token
     */
    setToken: (token: string): void => {
        localStorage.setItem('token', token);
    },

    /**
     * Store permissions
     */
    setPermissions: (permissions: any): void => {
        localStorage.setItem('permissions', JSON.stringify(permissions));
    },

    /**
     * Get stored permissions
     */
    getStoredPermissions: (): any => {
        const permissions = localStorage.getItem('permissions');
        return permissions ? JSON.parse(permissions) : null;
    },
};

export default authApi;
